// greatly inspired by: https://github.com/tomisu/react-element-scroll-hook
import { throttle } from "lodash";
import { RefObject, useCallback, useEffect, useMemo } from "react";
import { useIsomorphicLayoutEffect } from "react-use";
import useStateRef from "react-usestateref";

// small hook to use ResizeObserver if available, which fixes some issues when the component is resized
function useResizeObserver<
  T extends Element,
  F extends (...args: unknown[]) => unknown
>(ref: RefObject<T>, callback: F) {
  useEffect(() => {
    if (ref.current && typeof window !== "undefined" && window.ResizeObserver) {
      const resizeObserver = new ResizeObserver((entries) =>
        // wrapped in requestAnimationFrame to avoid this error: ResizeObserver loop limit exceeded
        window.requestAnimationFrame(() => entries.length && callback())
      );
      resizeObserver.observe(ref.current);
      return () => resizeObserver.disconnect();
    }

    return undefined;
  }, [callback, ref]);
}

export type OverflowingState = {
  isOverflowingLeft: boolean;
  isOverflowingRight: boolean;
};

/** Determines whether an element is currently overflowing horizontally. */
export default function useHorizontalScrollOverflow<T extends Element>(
  ref: RefObject<T>,
  { throttleWait = 50 } = {}
): OverflowingState {
  const [state, setState, stateRef] = useStateRef<OverflowingState>({
    isOverflowingLeft: false,
    isOverflowingRight: false,
  });

  // re-computes state
  const update = useCallback(() => {
    if (!ref.current) return;
    const element = ref.current;

    const maxScroll = element.scrollWidth - element.clientWidth;

    const scrollPercentage =
      maxScroll !== 0 ? element.scrollLeft / maxScroll : undefined;

    const isOverflowingLeft =
      scrollPercentage !== undefined && scrollPercentage > 0;
    const isOverflowingRight =
      scrollPercentage !== undefined && scrollPercentage < 1;

    // prevent unnecessary re-renders
    const { current: prevState } = stateRef;
    if (
      prevState.isOverflowingLeft !== isOverflowingLeft ||
      prevState.isOverflowingRight !== isOverflowingRight
    )
      setState({
        isOverflowingLeft,
        isOverflowingRight,
      });
  }, [ref, setState, stateRef]);

  // re-compute state on element resize
  useResizeObserver(ref, update);

  // throttled update to prevent staggering
  const throttledUpdate = useMemo(
    () => throttle(update, throttleWait),
    [throttleWait, update]
  );

  // register listeners
  useIsomorphicLayoutEffect(() => {
    if (!ref.current) return undefined;
    const element = ref.current;

    // update on scroll
    element.addEventListener("scroll", throttledUpdate);

    // update on window resize as a fallback if ResizeObserver is not available
    if (!window.ResizeObserver)
      window.addEventListener("resize", throttledUpdate);

    return () => {
      // clean up listeners
      element.removeEventListener("scroll", throttledUpdate);
      if (!window.ResizeObserver)
        window.removeEventListener("resize", throttledUpdate);
    };
  }, [ref, throttledUpdate]);

  return state;
}
